/* eslint react-hooks/exhaustive-deps: "warn" */

import { Fragment, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { AnimatedNumber } from 'ui/atoms/AnimatedNumber/AnimatedNumber';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './RegisComparison.module.scss';

type Translate = (key: string) => string;

type Row = {
  label: string;
  own: string;
  other: string;
  short?: boolean;
};

const getRows = (t: Translate): Row[] => [
  {
    label: t('Source of the answer'),
    own: t('Registry records retrieved at request time, with no company facts held in weights'),
    other: t('Records passed into a model context, assembled by a model that also answers from training'),
  },
  {
    label: t('Citation level'),
    own: t('Field level. Each value carries its register, reference and retrieval time'),
    other: t('Document level, where the system cites at all'),
  },
  {
    label: t('Missing field'),
    own: t('Returns null and names the register that is silent'),
    other: t("Depends on the assistant's behaviour"),
  },
  { label: t('Commercial contracts'), own: t('One'), other: t('Two'), short: true },
  { label: t('Data processing agreements'), own: t('One'), other: t('Two'), short: true },
  { label: t('Assistant seat licences'), own: t('None'), other: t('Required'), short: true },
  { label: t('Pricing basis'), own: t('Per result'), other: t('Per token consumed'), short: true },
  { label: t('Bulk retrieval'), own: t('Direct, no inference'), other: t('Through the model context'), short: true },
  { label: t('Data path'), own: t('Single supplier'), other: t('Crosses a third party'), short: true },
  {
    label: t('Vendor review'),
    own: t('One vendor on the register'),
    other: t('Two vendors to assess, two answers on where the data travelled'),
  },
];

const getScale = (t: Translate) => [
  { value: 1250, bar: 42, label: t('Figures'), desc: t('Five years of accounts for one company') },
  {
    value: 19000,
    bar: 59,
    prefix: '~',
    label: t('Tokens'),
    desc: t('What those figures cost to put in front of the model'),
    mult: '×15',
  },
  {
    value: 19000000,
    bar: 100,
    prefix: '~',
    label: t('Tokens'),
    desc: t('The same request across a thousand companies'),
    mult: '×1,000',
  },
];

const getRouteOnePoints = (t: Translate): string[] => [
  t(
    'Every figure is pulled into the model’s context before it reaches you — the retrieval itself is priced as reasoning',
  ),
  t('Ask a follow-up and the same context is sent again and billed again'),
  t('On top of the token charge sits the assistant subscription, plus a seat for everyone who uses it'),
  t('A second commercial contract and a second data processing agreement'),
];

const getRouteTwoPoints = (t: Translate): string[] => [
  t('The same figures come straight out of the register as structured data, with no model in the middle'),
  t('No tokens involved'),
  t('Charged per result rather than per token, so a thousand-company pull costs what moving data costs'),
  t('No subscription, no seats — one contract, one agreement'),
];

export const RegisComparison = () => {
  const { t } = useInt();
  const rootRef = useRef<HTMLDivElement>(null);
  const visible = useIntersection(rootRef, { threshold: 0.1 });

  const rows = getRows(t);
  const scale = getScale(t);
  const routeOnePoints = getRouteOnePoints(t);
  const routeTwoPoints = getRouteTwoPoints(t);

  return (
    <Section className={styles.root}>
      <div className={styles.glowBlue} />
      <div className={styles.glowIndigo} />

      <div ref={rootRef} className={cn(styles.content, visible && styles.contentIn)}>
        <div className={styles.head}>
          <TitleBlock
            title={t('Reaching the same records through a general assistant.')}
            description={t(
              'Company data can be pulled into a general-purpose model through a connector. It works. What it changes is how many suppliers sit between the question and the register, what the retrieval is billed as, and how much of the answer a reviewer can trace.',
            )}
            align="left"
          />
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th />
                <th className={styles.ownHead}>Regis API</th>
                <th>{t('Data provider plus a general assistant')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label}>
                  <td className={styles.rowLabel}>{row.label}</td>
                  <td className={styles.own} data-label="Regis API">
                    {row.short ? (
                      <span className={styles.pillOwn}>
                        <span className={styles.pillCheck}>&#10003;</span>
                        {row.own}
                      </span>
                    ) : (
                      row.own
                    )}
                  </td>
                  <td className={styles.other} data-label={t('Provider + assistant')}>
                    {row.short ? <span className={styles.pillOther}>{row.other}</span> : row.other}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className={styles.note}>{t('Both columns return the same registry records.')}</p>

        <div className={styles.cost}>
          <p className={styles.costLead}>
            {t(
              'Ask a general-purpose AI assistant for five years of financials on one company. Its accounts carry about 250 separate figures a year, so',
            )}{' '}
            <strong>{t('1,250 figures have to travel into the model’s context window')}</strong>{' '}
            {t(
              'before it can say anything. That is roughly 19,000 tokens, billed at inference rates, to move data that was already structured.',
            )}
          </p>

          <div className={styles.scale}>
            {scale.map((item, i) => (
              <Fragment key={item.label + item.value}>
                {i > 0 && (
                  <div className={styles.scaleConnector} aria-hidden="true">
                    {item.mult && <span className={styles.scaleMult}>{item.mult}</span>}

                    <svg className={styles.scaleArrow} width="20" height="10" viewBox="0 0 20 10" fill="none">
                      <path
                        d="M1 5H16M16 5L12 1M16 5L12 9"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}

                <div className={styles.scaleItem} style={{ animationDelay: `${i * 110}ms` }}>
                  <span className={styles.scaleNumRow}>
                    {item.prefix && <span className={styles.scaleApprox}>{item.prefix}</span>}
                    <AnimatedNumber value={item.value} duration={1400} className={styles.scaleNum} />
                  </span>

                  <span className={styles.scaleBar} aria-hidden="true">
                    <span
                      className={styles.scaleBarFill}
                      style={{ width: visible ? `${item.bar}%` : 0, transitionDelay: `${300 + i * 110}ms` }}
                    />
                  </span>

                  <span className={styles.scaleTag}>{item.label}</span>

                  <span className={styles.scaleLabel}>{item.desc}</span>
                </div>
              </Fragment>
            ))}
          </div>

          <div className={styles.costSplit}>
            <div className={styles.costCol}>
              <span className={styles.costColHead}>
                <span className={styles.costColNum}>01</span>
                {t('A data provider plus a general assistant')}
              </span>
              <ul className={styles.costList}>
                {routeOnePoints.map((point) => (
                  <li key={point}>
                    <span className={styles.costIconOther} aria-hidden="true">
                      <svg width="7" height="7" viewBox="0 0 7 7" fill="none">
                        <path
                          d="M1.2 1.2L5.8 5.8M5.8 1.2L1.2 5.8"
                          stroke="currentColor"
                          strokeWidth="1.4"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.vsBadge} aria-hidden="true">
              VS
            </div>

            <div className={cn(styles.costCol, styles.costColOwn)}>
              <span className={styles.costColHead}>
                <span className={styles.costColNum}>02</span>
                {t('The Regis API')}
              </span>
              <ul className={styles.costList}>
                {routeTwoPoints.map((point) => (
                  <li key={point}>
                    <span className={styles.costIconOwn} aria-hidden="true">
                      <svg width="8" height="7" viewBox="0 0 8 7" fill="none">
                        <path
                          d="M1 3.6L3 5.6L7 1.2"
                          stroke="currentColor"
                          strokeWidth="1.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
